import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Phone, ChevronRight, User, Globe, Grid, Map as MapIcon, Loader2, ArrowRight } from 'lucide-react';
import styles from './BranchesPage.module.css';
import logoImg from '../../assets/images/anapurna_logo_transparent.png';
import { useBranches } from '../../hooks/useBranches';

const BranchesPage = () => {
  const { branches, loading } = useBranches();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const navigate = useNavigate();

  if (loading && branches.length === 0) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  if (!loading && branches.length === 0) {
    return (
      <main className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.container}>
            <h1 className={styles.heroTitle}>Our Presence Across India</h1>
            <p className={styles.heroSubtitle}>No branch data available at the moment.</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      {/* 🔝 1. Hero Section */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.breadcrumb}>
            <Link to="/">Home</Link>
            <ChevronRight size={14} className={styles.breadcrumbIcon} />
            <span>Branches</span>
          </div>
          <h1 className={styles.heroTitle}>Our Presence Across India</h1>
          <p className={styles.heroSubtitle}>
            Serving retailers across 250+ cities with our strategic showroom networks.
          </p>

          {/* View Toggle */}
          <div className={styles.viewToggle}>
            <button 
              className={`${styles.toggleBtn} ${viewMode === 'grid' ? styles.activeToggle : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={18} />
              <span>Grid View</span>
            </button>
            <button 
              className={`${styles.toggleBtn} ${viewMode === 'map' ? styles.activeToggle : ''}`}
              onClick={() => setViewMode('map')}
            >
              <MapIcon size={18} />
              <span>Map View</span>
            </button>
          </div>
        </div>
      </section>

      {/* 🏙️ 2. Main Content Area */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          
          {viewMode === 'grid' ? (
            <div className={styles.masonryGrid}>
              {branches.map((loc, index) => (
                <div 
                  key={loc.id} 
                  onClick={() => navigate(`/collections?branch=${loc.id}&branchName=${encodeURIComponent(loc.city)}`)}
                  className={`
                    ${styles.branchCard} 
                    ${loc.size === 'large' ? styles.cardLarge : ''} 
                    ${loc.size === 'tall' ? styles.cardTall : ''} 
                    ${loc.isHeadOffice ? styles.headOfficeCard : ''}
                    reveal
                  `}
                  style={{ animationDelay: `${index * 0.1}s`, cursor: 'pointer' }}
                >
                  {/* Background Image & Elite Overlay */}
                  <div className={styles.cardBg}>
                    <img src={loc.image} alt={loc.city} className={styles.cityImg} />
                    <div className={styles.bgOverlay} />
                  </div>

                  {/* Logo Watermark (Top Right) */}
                  <div className={styles.cardWatermark}>
                    <img src={logoImg} alt="" className={styles.watermarkLogo} />
                  </div>

                  {/* Floating Action Button (Top Right) */}
                  <a href={`tel:${loc.phones[0]}`} className={styles.floatingCall} title="Call Branch">
                    <Phone size={18} />
                  </a>

                  {/* Content (Refined Hierarchy) */}
                  <div className={styles.cardContent}>
                    {loc.isHeadOffice && (
                      <span className={styles.hoBadge}>
                        <Globe size={10} style={{marginRight: '6px'}} />
                        HEAD OFFICE
                      </span>
                    )}
                    
                    <h2 className={styles.cityName}>{loc.city}</h2>
                    
                    <div className={styles.personRow}>
                       <User size={14} className={styles.accentIcon} />
                       <span className={styles.personName}>{loc.person}</span>
                    </div>

                    <div className={styles.bottomDetails}>
                      <div className={styles.detailItem}>
                        <MapPin size={14} className={styles.detailIcon} />
                        <span className={styles.addressText}>{loc.address}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <Phone size={14} className={styles.detailIcon} />
                        <span className={styles.phoneText}>{loc.phones.join(' / ')}</span>
                      </div>
                    </div>

                    <div className={styles.branchCtaBtn}>
                      <span>Explore Products</span>
                      <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.mapContainer}>
              <div className={styles.mapPlaceholder}>
                <div className={styles.mapInner}>
                  <MapIcon size={48} className={styles.mapIcon} />
                  <h3>Interactive Map Coming Soon</h3>
                  <p>We are currently integrating Google Maps API to provide a real-time branch navigator.</p>
                  <button onClick={() => setViewMode('grid')} className={styles.backToGrid}>Return to Grid View</button>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* 🚀 3. Luxury CTA Section */}
      <section className={styles.ctaBanner}>
        <div className={styles.container}>
            <div className={styles.ctaBox}>
                <div className={styles.ctaContent}>
                  <div className={styles.ctaHeader}>
                    <Globe size={32} className={styles.ctaIcon} />
                    <h2>Wholesale Network</h2>
                  </div>
                  <p>Can't find a branch in your city? We offer pan-India express shipping and dedicated support for new retailers looking to join the Anapurna family.</p>
                  <div className={styles.ctaActions}>
                    <Link to="/contact" className={styles.primaryCta}>Get Assistance</Link>
                    <a href="tel:9212150288" className={styles.secondaryCta}>Call Helpline</a>
                  </div>
                </div>
                <div className={styles.ctaImage}>
                   <img src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80" alt="Logistics" />
                </div>
            </div>
        </div>
      </section>
    </main>
  );
};

export default BranchesPage;
